import React from 'react';
import PropTypes from 'prop-types';
import { get, find, pickBy } from 'lodash';
import { BackButton } from 'web-ui-components/lib/molecules/navigation';
import { StandardBody } from 'web-ui-components/lib/typography/body';
import { renderAccountDetails } from '../ViewPaymentHistory';
import ViewChildPaymentHistory from './ViewChildPaymentHistory';
import PrintHelper from 'print-helper-lib';
import StatusBadgeSME from './StatusBadgeSME';
import config from '../../../config';
import NumberUtils from '../../../utils/NumberUtils';
import { PRE_BOOKED_TYPE } from '../../../constants/StringConstants';
import { VerticalReviewTable } from 'web-ui-components/lib/layout/tables';
import {
  SubHeading,
  SectionHeading,
} from 'web-ui-components/lib/typography/headings';
import { NavigationButton } from 'web-ui-components/lib/navigation/buttons';

const brandPathMapping = {
  b: 'DYB',
  cb: 'CB',
  yb: 'YB',
  vm: 'VM',
};

const getBrandId = () => {
  const path = window.location.pathname.slice(1);
  const slashPosition = path.indexOf('/');
  const brand = slashPosition === -1 ? path : path.substring(0, slashPosition);
  return brandPathMapping[`${brand}`.toLowerCase()];
};

export const getPaymentFees = (fees, chargingModal) => {
  const getChargingModalContent = get(
    find(config.whoPaysMapping, (whoPays) => whoPays.id === chargingModal),
    'type'
  );
  return `${fees} (${getChargingModalContent})`;
};

export const addGBPCurrencyCode = (amount) =>
  amount.indexOf('£') > -1 ? `${amount} GBP` : amount;

export const getIFTHistoryDetails = (historyDetails, content) => {
  const {
    paymentType,
    debitAmount,
    paymentFees,
    submissionDate,
    indicativeExchangeRate,
    actualExchangeRate,
    amount,
    settlementDate,
    fromAccountAccountNumber,
    fromAccountSortCode,
    fromAccountName,
    toAccountAccountNumber,
    beneficiaryCountry,
    toAccountName,
    reference,
    chargingModal,
    chargeAmount,
    amountCurrency,
    amountRaw,
    deal_reference,
    indicativeRateType,
    actualRateType,
  } = historyDetails;

  const getAmountToSend = () => {
    if (!(chargingModal === config.chargingModel.CRED))
      return addGBPCurrencyCode(amount);
    const amountMinusFees =
      Number.parseFloat(amountRaw) - Number.parseFloat(chargeAmount);
    const formattedAmount = NumberUtils.appendCurrency(
      '{}',
      amountMinusFees,
      amountCurrency,
      undefined,
      true
    );

    return addGBPCurrencyCode(formattedAmount);
  };
  const getExchangeRate = () => {
    return actualExchangeRate ? actualExchangeRate : indicativeExchangeRate;
  };
  const isPreBookedRate = () => {
    return actualExchangeRate
      ? actualRateType === PRE_BOOKED_TYPE
      : indicativeRateType === PRE_BOOKED_TYPE;
  };
  const displayToAccount = `${toAccountName} / ${toAccountAccountNumber} / ${beneficiaryCountry}`;

  const tableRowMapping = pickBy({
    [content.typeLabel]: paymentType,
    [content.fromLabel]: renderAccountDetails(
      fromAccountAccountNumber,
      fromAccountSortCode,
      fromAccountName
    ),
    [content.toLabel]: displayToAccount,
    [content.debitAmountLabel]: addGBPCurrencyCode(debitAmount),
    [content.paymentFeesLabel]: getPaymentFees(paymentFees, chargingModal),
    [content.submissionDateLabel]: submissionDate,
    [content.exchangeRateLabel]: isPreBookedRate()
      ? `${getExchangeRate()} (${deal_reference})`
      : getExchangeRate(),
    [content.sendAmountLabel]: getAmountToSend(),
    [content.settlementDateLabel]: settlementDate,
    ...(reference && { [content.referenceLabel]: reference }),
  });
  return tableRowMapping;
};
export const getCreatAndAuthoriseDetails = (historyDetails, content) => {
  const { authorisedBy, createdBy, createdDate } = historyDetails;
  const tableRowMapping = {
    ...(createdBy && { [content.createdByLabel]: createdBy }),
    ...(createdDate && { [content.createdOnLabel]: createdDate }),
    ...(authorisedBy && { [content.authorisedByLabel]: authorisedBy }),
  };
  return tableRowMapping;
};
const ViewIFTHistory = ({
  brandId = getBrandId(),
  historyDetails,
  statusMsg,
  goBack,
  content,
  parentPayment = false,
}) => {
  const tableRowMapping = getIFTHistoryDetails(historyDetails, content);
  const createAndAuthoriseTableRowMapping = getCreatAndAuthoriseDetails(
    historyDetails,
    content
  );

  const statusData = [
    {
      headerText: 'Status',
      cellText: (
        <div>
          <StatusBadgeSME status={historyDetails.status} />
          <StandardBody>
            <p>{statusMsg}</p>
          </StandardBody>
        </div>
      ),
    },
  ];

  const paymentData = () =>
    Object.keys(tableRowMapping).map((tableRowKey) => ({
      headerText: tableRowKey,
      cellText: tableRowMapping[tableRowKey],
    }));

  const paymentDataAuthorise = () =>
    Object.keys(createAndAuthoriseTableRowMapping).map((tableRowKey) => ({
      headerText: tableRowKey,
      cellText: createAndAuthoriseTableRowMapping[tableRowKey],
    }));

  return (
    <div className="web-ui-components">
      <BackButton onClick={goBack} />
      <SubHeading>Payment status</SubHeading>
      <div className="u-component-spacing">
        <div className="u-margin-bottom-4">
          <SectionHeading>Bank acknowledgement details</SectionHeading>
          <PrintHelper
            className="web-ui-components"
            brandId={brandId}
            showPrintTopIcon
            showPrintBottomIcon={false}
          />
          <VerticalReviewTable data={statusData} />
        </div>
        <div className="u-margin-bottom-2">
          <SectionHeading>Payment details</SectionHeading>

          <VerticalReviewTable data={paymentData()} />
          <VerticalReviewTable data={paymentDataAuthorise()} />
        </div>
        {parentPayment && historyDetails.children && (
          <ViewChildPaymentHistory historyDetails={historyDetails} />
        )}
        <NavigationButton.Secondary labelText="Back" onClick={goBack} />
      </div>
    </div>
  );
};

ViewIFTHistory.propTypes = {
  statusMsg: PropTypes.string.isRequired,
  historyDetails: PropTypes.shape({
    paymentType: PropTypes.string.isRequired,
    debitAmount: PropTypes.string.isRequired,
    paymentFees: PropTypes.string.isRequired,
    submissionDate: PropTypes.string.isRequired,
    indicativeExchangeRate: PropTypes.string.isRequired,
    actualExchangeRate: PropTypes.string.isRequired,
    settlementDate: PropTypes.string.isRequired,
    authorisedBy: PropTypes.string.isRequired,
    createdBy: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
    fromAccountAccountNumber: PropTypes.string.isRequired,
    fromAccountSortCode: PropTypes.string.isRequired,
    fromAccountName: PropTypes.string.isRequired,
    toAccountAccountNumber: PropTypes.string.isRequired,
    toAccountSortCode: PropTypes.string.isRequired,
    toAccountName: PropTypes.string.isRequired,
    reference: PropTypes.string.isRequired,
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  content: PropTypes.shape({}),
};

export default ViewIFTHistory;
